<template lang="html">
    <div class="annual-hydrology-table">
        <b-table
            :data="formattedData"
            :is-fullwidth="true"
            :narrowed="true"
            :mobile-cards="false"
        >
            <template slot-scope="props">
                <b-table-column
                    field="rowName"
                    label="Annual Statistics"
                >
                    <div v-if="props.row.footnote">
                        {{ props.row.rowName }}<sup>
                            <footnote-tooltip
                                :footnote-number="props.row.footnote"
                            />
                        </sup>
                    </div>
                    <div v-else>
                        {{ props.row.rowName }}
                    </div>
                </b-table-column>
                <b-table-column
                    field="qw"
                    :label="data.queryWatershedName"
                    numeric
                >
                    <template
                        slot="header"
                        slot-scope="{ column }"
                        class="query-watershed"
                    >
                        <div class="query-watershed">
                            <map-marker
                                v-if="!pdf"
                                class="map-marker"
                            />
                            {{ column.label }}
                        </div>
                    </template>
                    {{ props.row.qw }}
                </b-table-column>
                <b-table-column
                    field="dw"
                    class="is-hidden-mobile"
                    :label="data.downstreamWatershedName"
                    numeric
                >
                    <template
                        slot="header"
                        slot-scope="{ column }"
                    >
                        <div class="is-hidden-mobile downstream-watershed">
                            <map-marker
                                v-if="!pdf"
                                class="map-marker"
                            />
                            {{ column.label }}
                        </div>
                    </template>
                    {{ props.row.dw }}
                </b-table-column>
            </template>
        </b-table>
    </div>
</template>

<script>
import FootnoteTooltip from '@foundryspatial/footnote-tooltip';
import MapMarker from '@foundryspatial/map-marker';
import numeral from 'numeral';

export default {
    components: {
        FootnoteTooltip,
        MapMarker,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        pdf: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        formattedData() {
            const rows = [
                {
                    rowName: 'Area (km²)',
                    qw: this.formatValue(this.data.area_km2.query, 0),
                    dw: this.formatValue(this.data.area_km2.downstream, 0),
                },
                {
                    rowName: 'Mean Annual Discharge (MAD, m³/s)',
                    qw: this.formatValue(this.data.mad_m3s.query, 3),
                    dw: this.formatValue(this.data.mad_m3s.downstream, 3),
                },
                {
                    rowName: 'Allocations (average, m³/s)',
                    qw: this.formatValue(this.data.allocs_m3s.query, 3),
                    dw: this.formatValue(this.data.allocs_m3s.downstream, 3),
                    footnote: 9,
                },
                {
                    rowName: 'Allocations (average, % of MAD)',
                    qw: this.formatValue(this.data.allocs_pct.query, 1),
                    dw: this.formatValue(this.data.allocs_pct.downstream, 1),
                },
                {
                    rowName: 'Reserves & Restrictions',
                    qw: this.data.rr.query,
                    dw: this.data.rr.downstream,
                    footnote: 4,
                },
                {
                    rowName: 'Volume Runoff (m³/yr)',
                    qw: this.formatValue(this.data.runoff_m3yr.query, 0),
                    dw: this.formatValue(this.data.runoff_m3yr.downstream, 0),
                },
                {
                    rowName: 'Volume Allocations (m³/yr)',
                    qw: this.formatValue(this.data.allocs_m3yr.query, 0),
                    dw: this.formatValue(this.data.allocs_m3yr.downstream, 0),
                },
                {
                    rowName: 'Seasonal Flow Sensitivity',
                    qw: this.data.seasonal_sens.query,
                    dw: this.data.seasonal_sens.downstream,
                    footnote: 5,
                },
            ];
            return rows;
        },
    },
    methods: {
        /**
         * Format a number, rounding the decimal places and adding commas to large numbers
         *
         * @param {number} value - number to be formatted
         * @param {number} decimalPlaces - number of decimal places to round to. Default is 2
         * @returns {string} - number properly formatted as string
         */
        formatValue(value, decimalPlaces = 2) {
            // Any value less than 0.1 has already been formatted as a string by the db
            if (value === '< 0.1') {
                return value;
            }

            const zeroes = '0'.repeat(decimalPlaces);

            // to format with numeral, we pass a string in the "format" that we want
            // to put commas for thousands/millions/etc, we put '0,0'
            // to round to certain decimal place, we put that many zeroes after the decimal
            return numeral(value).format(`0,0.${zeroes}`);
        },
        /**
         * Join an array into a string to be displayed.
         *
         * @param {Array} values - array of values to be joined together
         * @returns {string} - given values joined into a string or 'None' if there are no values
         */
        listJoin(values) {
            if (values && values.length > 0) {
                return values.join(', ');
            }
            return 'None';
        },
    },
};
</script>

<style lang="scss">
.annual-hydrology-table {
    th {
        .map-marker {
            display: inline-block;
            width: 1em;
            margin-bottom: -0.45em;
            margin-right: 0.25em;
        }

        .query-watershed {
            color: $color-watershed-query;

            .map-marker .color-fill {
                fill: $color-watershed-query;
            }
        }

        .downstream-watershed {
            color: $color-watershed-mgmt;

            .map-marker .color-fill {
                fill: $color-watershed-mgmt;
            }
        }
    }

    td {
        width: 25%;
        padding-top: 0.2em !important;
        padding-bottom: 0.2em !important;

        @media screen and (max-width: $tablet) {
            width: 50%;
        }

        &:first-child {
            width: 50%;
            color: grey;
        }
    }
}
</style>
